import { FunctionComponent, useState } from 'react'
import styles from './adminList.module.scss'
import Title from '@/components/_custom/Title/title'
import { Role } from '@/types/Role'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Desktop from '@/modules/desktopMobile/desktop'
import { QuickSearchToolbar, StyledDataGrid } from '@/components/_custom/DataGrid'
import { frFR } from '@mui/x-data-grid/locales'
import Mobile from '@/modules/desktopMobile/mobile'
import { useTranslation } from 'react-i18next'
import { GridColDef } from '@mui/x-data-grid'
import { Check, Close, Delete, Edit, LinkOff } from '@mui/icons-material'
import { DaycareTrustee } from '@/types/DaycareGroup'
import AddAdminDialog from './Dialog/addAdminDialog'
import EditAdminDialog from './Dialog/editAdminDialog'
import DeleteAdminDialog from './Dialog/deleteAdminDialog'
import UnlinkAdminDialog from './Dialog/unlinkAdminDialog'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AdminListProps {
    role?: Role
    groupId: number
    trusteeData: DaycareTrustee[]
    refetchGroups: () => void
}

const AdminList: FunctionComponent<AdminListProps> = ({
    role,
    groupId,
    trusteeData,
    refetchGroups
}: AdminListProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess } = useNotification()

    const [isAddTrusteeDialogOpen, setAddTrusteeDialogOpen] = useState<boolean>(false)
    const [isEditTrusteeDialogOpen, setEditTrusteeDialogOpen] = useState<boolean>(false)
    const [isDeleteTrusteeDialogOpen, setDeleteTrusteeDialogOpen] = useState<boolean>(false)
    const [isUnlinkTrusteeDialogOpen, setUnlinkTrusteeDialogOpen] = useState<boolean>(false)

    const headerClassName: string = role === Role.parent ? styles.HeaderParent : styles.HeaderDaycare

    const trusteeColumnsCALDWeb: GridColDef[] = [
        {
            field: 'id',
            headerName: t('adminID'),
            headerClassName: headerClassName,
            width: 30,
            maxWidth: 50,
            minWidth: 30,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'firstname',
            headerName: t('firstname'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 300,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'lastname',
            headerName: t('lastname'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 300,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 300,
            maxWidth: 500,
            minWidth: 300,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 200,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'main',
            headerName: t('isMainAdmin'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 250,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.CheckContainer}>
                    {params.row.main ? <Check className={styles.GreenIcon} /> : <Close className={styles.RedIcon} />}
                </div>
            )
        },
        {
            field: 'canWrite',
            headerName: t('canWrite'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 250,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.CheckContainer}>
                    {params.row.canWrite ? (
                        <Check className={styles.GreenIcon} />
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 250,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <Edit onClick={() => openTrusteeEdit(params.row)} className={styles.Icon} />
                    <LinkOff onClick={() => openTrusteeUnlink(params.row)} className={styles.Icon} />
                    <Delete onClick={() => openTrusteeDelete(params.row)} className={styles.Icon} />
                </div>
            )
        }
    ]

    const trusteeColumnsCALMobile: GridColDef[] = [
        {
            field: 'lastname',
            headerName: t('lastname'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 500,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 600,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 250,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <Edit onClick={() => openTrusteeEdit(params.row)} className={styles.Icon} />
                    <LinkOff onClick={() => openTrusteeUnlink(params.row)} className={styles.Icon} />
                    <Delete onClick={() => openTrusteeDelete(params.row)} className={styles.Icon} />
                </div>
            )
        }
    ]

    const trusteeColumnsWeb: GridColDef[] = [
        {
            field: 'firstname',
            headerName: t('firstname'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 500,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'lastname',
            headerName: t('lastname'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 500,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 600,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 600,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        }
    ]

    const trusteeColumnsMobile: GridColDef[] = [
        {
            field: 'lastname',
            headerName: t('lastname'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 500,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 250,
            maxWidth: 600,
            minWidth: 250,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        }
    ]

    const [editTrustee, setEditTrustee] = useState<DaycareTrustee | null>(null)

    function openTrusteeEdit(trustee: DaycareTrustee): void {
        if (trustee) {
            setEditTrustee(trustee)
            setEditTrusteeDialogOpen(true)
        } else {
            showNotificationSuccess(t('noAdmins'))
        }
    }

    function openTrusteeDelete(trustee: DaycareTrustee): void {
        if (trustee) {
            setEditTrustee(trustee)
            setDeleteTrusteeDialogOpen(true)
        } else {
            showNotificationSuccess(t('noAdmins'))
        }
    }

    function openTrusteeUnlink(trustee: DaycareTrustee): void {
        if (trustee) {
            setEditTrustee(trustee)
            setUnlinkTrusteeDialogOpen(true)
        } else {
            showNotificationSuccess(t('noAdmins'))
        }
    }

    return (
        <div className={styles.Container}>
            <div className={styles.TitleAndButton}>
                <div className={styles.Title}>
                    <Title text={t('adminList')} />
                </div>
                {role === Role.cald && (
                    <>
                        <ButtonTextIcon
                            type={'add'}
                            onClick={() => setAddTrusteeDialogOpen(true)}
                            className={styles.Button}
                        />
                        {isAddTrusteeDialogOpen && (
                            <AddAdminDialog
                                open={isAddTrusteeDialogOpen}
                                groupId={groupId}
                                handleClose={() => setAddTrusteeDialogOpen(false)}
                                refetchGroups={refetchGroups}
                            />
                        )}
                    </>
                )}
            </div>
            <div className={styles.AdminInformations}>
                <Desktop>
                    {role === Role.cald ? (
                        <StyledDataGrid
                            autoHeight
                            columns={trusteeColumnsCALDWeb}
                            rows={trusteeData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                        />
                    ) : (
                        <StyledDataGrid
                            autoHeight
                            columns={trusteeColumnsWeb}
                            rows={trusteeData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                        />
                    )}
                </Desktop>
                <Mobile>
                    {role === Role.cald ? (
                        <StyledDataGrid
                            autoHeight
                            columns={trusteeColumnsCALMobile}
                            rows={trusteeData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                        />
                    ) : (
                        <StyledDataGrid
                            autoHeight
                            columns={trusteeColumnsMobile}
                            rows={trusteeData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                        />
                    )}
                </Mobile>
            </div>
            {isEditTrusteeDialogOpen && (
                <EditAdminDialog
                    open={isEditTrusteeDialogOpen}
                    trusteeData={editTrustee}
                    handleClose={() => setEditTrusteeDialogOpen(false)}
                    refetchGroups={refetchGroups}
                    groupId={groupId}
                />
            )}
            {isDeleteTrusteeDialogOpen && (
                <DeleteAdminDialog
                    open={isDeleteTrusteeDialogOpen}
                    trusteeId={editTrustee?.id || 0}
                    handleClose={() => setDeleteTrusteeDialogOpen(false)}
                    refetchGroups={refetchGroups}
                />
            )}
            {isUnlinkTrusteeDialogOpen && (
                <UnlinkAdminDialog
                    open={isUnlinkTrusteeDialogOpen}
                    trusteeId={editTrustee?.id || 0}
                    groupId={groupId}
                    handleClose={() => setUnlinkTrusteeDialogOpen(false)}
                    refetchGroups={refetchGroups}
                />
            )}
        </div>
    )
}

export default AdminList
